import { describeApiError, type SalaryModel } from '@brush/api-client';
import {
  ErrorNotice,
  ErrorRetry,
  SalaryModelList,
  Spinner,
  currentSalaryVersion,
  latestPublishedSalaryVersion,
} from '@brush/ui';
import { useEffect, useMemo, useState } from 'react';
import {
  useDeleteSalaryModel,
  useFantasyContests,
  useSalaryModels,
  useSetSalaryModelArchived,
} from '../../queries';
import { useRoom } from '../../room';
import { useMainScroll } from '../../scroll';
import { useUnsaved } from '../../unsaved';
import { Modal, btnGhost, btnPrimary } from '../Modal';
import { NewSalaryModelDialog, type NewSalaryModelIntent } from './NewSalaryModelDialog';
import { SalaryModelBuilderScreen } from './SalaryModelBuilderScreen';

/**
 * The room's salary models: what it has built, plus everything the platform offers. §9.1's landing
 * screen for the pricing half of fantasy.
 *
 * <b>One pane of Settings</b>, so this renders its content and nothing around it — the page
 * heading, the sub-navigation and the margins belong to `SettingsView`, exactly as they do for
 * Templates and Points Systems. A salary model is set up once and revisited when something
 * changes, which is what the panes beside it are; it is not a day's work the way the tournaments
 * list or a contest is.
 *
 * <b>And it is not the contest screen.</b> A model is authored here without reference to any
 * contest, and a contest picks up a published one when it publishes. Keeping the two apart is what
 * lets one model price a room's whole season — the same separation, for the same reason, that
 * keeps points systems off the leaderboard screen.
 */
export function SalaryModelsPane() {
  const { room, roomId } = useRoom();
  const { leave } = useUnsaved();
  const scrollToTop = useMainScroll();

  const [includeArchived, setIncludeArchived] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [intent, setIntent] = useState<NewSalaryModelIntent | null>(null);
  const [deleting, setDeleting] = useState<SalaryModel | null>(null);

  const models = useSalaryModels(roomId, includeArchived);
  const contests = useFantasyContests(roomId);
  const archive = useSetSalaryModelArchived(roomId);

  const rows = useMemo(() => models.data ?? [], [models.data]);

  // Opening or closing the builder swaps a whole screen, and the two are wildly different
  // heights. Without this the operator lands part-way down a shorter one, looking at blank space.
  useEffect(() => {
    scrollToTop();
  }, [scrollToTop, openId]);

  /**
   * How many of *this room's* contests use each model.
   *
   * Counted rather than asked for: `SalaryModelDto` carries no usage figure, and a platform
   * model's total across every room is not a number this room can see or should be shown. A
   * contest names the *version* it pinned, not the model, so the version ids on the catalogue rows
   * are what resolve one to the other — which is also why this is null while either query is still
   * out. Null renders "—", and "no contest uses this" is a different answer from "this screen
   * doesn't know".
   */
  const usage = useMemo(() => {
    if (!contests.data) return null;

    const modelOfVersion = new Map<string, string>();
    for (const model of rows) {
      for (const version of model.versions) modelOfVersion.set(version.id, model.id);
    }

    const counts = new Map<string, number>();
    for (const contest of contests.data) {
      const modelId = contest.salaryModelVersionId
        ? modelOfVersion.get(contest.salaryModelVersionId)
        : undefined;
      if (modelId) counts.set(modelId, (counts.get(modelId) ?? 0) + 1);
    }

    return new Map(rows.map((m) => [m.id, counts.get(m.id) ?? 0]));
  }, [contests.data, rows]);

  if (!room) return null;

  const open = openId ? (rows.find((m) => m.id === openId) ?? null) : null;

  // Looked up by id every render rather than held, so the builder sees a new version the moment
  // the catalogue refetches. Leaving routes through the unsaved-work guard, exactly as the
  // section nav and the room switcher do.
  if (open) {
    return (
      <>
        <SalaryModelBuilderScreen
          model={open}
          onBack={() => leave(() => setOpenId(null))}
          onDiscarded={() => setOpenId(null)}
          onCloneRequest={() =>
            setIntent({
              mode: 'clone',
              source: open,
              versionNumber:
                (latestPublishedSalaryVersion(open) ?? currentSalaryVersion(open))?.versionNumber ??
                null,
            })
          }
        />
        {intent ? (
          <NewSalaryModelDialog
            intent={intent}
            onClose={() => setIntent(null)}
            onCreated={(created) => {
              setIntent(null);
              setOpenId(created.id);
            }}
          />
        ) : null}
      </>
    );
  }

  return (
    <div>
      {/* No heading of its own: the sub-navigation names the pane and the list card below carries
          its own. What belongs here is the sentence that says what a salary model is for. */}
      <p className="mb-4 max-w-2xl text-sm text-ink-800/60">
        What each player costs to put on a fantasy roster. Build one, see the prices it produces
        from series you have already run, and publish it when it looks right — a contest picks one
        up when it publishes and keeps that exact version for good.
      </p>

      {models.isLoading ? (
        <Spinner label="Loading salary models…" />
      ) : models.isError ? (
        <ErrorRetry
          message="Couldn't load the salary models."
          onRetry={() => void models.refetch()}
        />
      ) : (
        <SalaryModelList
          models={rows}
          usage={usage}
          usageLabel="Your contests"
          usageHint="Fantasy contests in this room that have pinned a version of this model."
          includeArchived={includeArchived}
          onIncludeArchivedChange={setIncludeArchived}
          onNew={(start) => setIntent({ mode: 'create', start })}
          onOpen={(row) => setOpenId(row.id)}
          onClone={(row) => {
            const model = rows.find((m) => m.id === row.id);
            if (!model) return;
            // A clone copies the version a room adopting it would have been priced by — the newest
            // published one — falling back to the newest of any status for a model that has never
            // published.
            const source = latestPublishedSalaryVersion(model) ?? currentSalaryVersion(model);
            setIntent({ mode: 'clone', source: model, versionNumber: source?.versionNumber ?? null });
          }}
          onArchive={(row) => archive.mutate({ id: row.id, archived: true })}
          onUnarchive={(row) => archive.mutate({ id: row.id, archived: false })}
          onDelete={(row) => setDeleting(rows.find((m) => m.id === row.id) ?? null)}
          // §4.1: a platform model is visible and usable here, and editable by nobody in this
          // room. The list drops its editing controls rather than disabling them, and offers
          // "Clone to edit" in their place.
          canManage={(row) => !row.isPlatform}
          nameOf={(id) => rows.find((m) => m.id === id)?.name ?? null}
          notice={
            contests.isError ? (
              <p className="text-xs text-ink-800/55">
                Couldn’t read this room’s contests, so the usage column is blank rather than wrong.
              </p>
            ) : null
          }
        />
      )}

      <ErrorNotice message={describeApiError(archive.error)} className="mt-4" />

      {intent ? (
        <NewSalaryModelDialog
          key={intent.mode === 'create' ? `new-${intent.start}` : `clone-${intent.source.id}`}
          intent={intent}
          onClose={() => setIntent(null)}
          onCreated={(created) => {
            setIntent(null);
            setOpenId(created.id);
          }}
        />
      ) : null}

      {deleting ? (
        <DeleteDialog
          model={deleting}
          onClose={() => setDeleting(null)}
          onArchive={() => {
            archive.mutate({ id: deleting.id, archived: true });
            setDeleting(null);
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * Three outcomes the UI must not blur — the points catalogue's §2.4 rules, which §4.1 adopts.
 *
 * A model nothing has pinned is deleted outright. A model a contest has priced against cannot be,
 * and the server refuses with a 409 whose message leads with Archive rather than with the failure.
 * So Archive is offered *before* the attempt as well as after one: for any model that has actually
 * been used, archiving is what was wanted.
 */
function DeleteDialog({
  model,
  onClose,
  onArchive,
}: {
  model: SalaryModel;
  onClose: () => void;
  onArchive: () => void;
}) {
  const { roomId } = useRoom();
  const remove = useDeleteSalaryModel(roomId);

  return (
    <Modal
      title={`Delete ${model.name}?`}
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} className={btnGhost}>
            Cancel
          </button>
          <button type="button" onClick={onArchive} className={btnPrimary}>
            Archive instead
          </button>
          <button
            type="button"
            onClick={() => remove.mutate(model.id, { onSuccess: onClose })}
            disabled={remove.isPending}
            className="flex-1 rounded-lg border border-chip-500/40 py-2 text-sm font-medium text-chip-500 hover:bg-chip-500/5 disabled:opacity-40"
          >
            {remove.isPending ? 'Deleting…' : 'Delete'}
          </button>
        </>
      }
    >
      <div className="space-y-3 text-sm leading-relaxed text-ink-800/80">
        <p>
          <strong className="text-ink-950">Archiving</strong> hides it from the picker and leaves
          every contest that used it intact, with its prices exactly as they were. It can be
          restored.
        </p>
        <p>
          <strong className="text-ink-950">Deleting</strong> removes the model and all its versions
          for good, and only works if no contest has ever pinned one. If one has, the server will
          refuse — and archiving is what you wanted.
        </p>
        <ErrorNotice message={describeApiError(remove.error)} />
      </div>
    </Modal>
  );
}
